import { FunctionComponent, useCallback, useEffect, useLayoutEffect, useMemo, useRef } from 'react'
import { isOptimisticMessage } from '../../../model/StatsAssistant'
import { Loader, RoundedButton } from '../../shared'
import { AssistantTurn } from '../AssistantTurn'
import { MessageBubble } from '../MessageBubble'
import { StarterPanel } from '../StarterPanel'
import {
  findLastUserMessage,
  groupMessagesIntoTurns,
  hasMessagesAfter,
} from '../utils/groupMessages'
import { ChatThreadProps } from './ChatThread.model'
import styles from './ChatThread.module.scss'

const BOTTOM_THRESHOLD = 40

export const ChatThread: FunctionComponent<ChatThreadProps> = ({
  conversationUlid,
  scrollSnapshotRef,
  messages,
  isLoading = false,
  isProcessing,
  progressText,
  turnError,
  agentProfiles,
  agentProfile,
  onAgentProfileChange,
  compactChart = false,
  onSuggestionSelect,
  onChartExpand,
  onStop,
  onRetryLastTurn,
  onRetryTurnError = onRetryLastTurn,
  onEditUserMessage,
}) => {
  const threadRef = useRef<HTMLDivElement>(null)
  const turnsRef = useRef<HTMLDivElement>(null)
  const bottomRef = useRef<HTMLDivElement>(null)
  const skipAutoScrollRef = useRef(false)
  const turns = useMemo(() => groupMessagesIntoTurns(messages), [messages])
  const lastUser = useMemo(() => findLastUserMessage(messages), [messages])

  const isEmpty = messages.length === 0 && !isProcessing && !turnError

  const saveScrollSnapshot = useCallback(() => {
    const thread = threadRef.current
    if (!thread) return
    const threadTop = thread.getBoundingClientRect().top
    const isAtBottom =
      thread.scrollHeight - thread.scrollTop - thread.clientHeight <= BOTTOM_THRESHOLD
    const children = Array.from(turnsRef.current?.children ?? [])
    const anchorIndex = children.findIndex(
      (child) => child.getBoundingClientRect().bottom > threadTop,
    )
    scrollSnapshotRef.current = {
      conversationUlid,
      isAtBottom,
      anchorIndex,
      anchorOffset:
        anchorIndex >= 0 ? children[anchorIndex].getBoundingClientRect().top - threadTop : 0,
    }
  }, [conversationUlid, scrollSnapshotRef])

  // Le fil est remonté à chaque changement de taille : on rétablit la lecture là où elle était.
  useLayoutEffect(() => {
    const thread = threadRef.current
    const snapshot = scrollSnapshotRef.current
    if (!thread || isLoading || !snapshot || snapshot.conversationUlid !== conversationUlid) return

    skipAutoScrollRef.current = true
    const anchor = turnsRef.current?.children[snapshot.anchorIndex]
    if (snapshot.isAtBottom || !anchor) {
      thread.scrollTop = thread.scrollHeight
      return
    }
    thread.scrollTop +=
      anchor.getBoundingClientRect().top -
      thread.getBoundingClientRect().top -
      snapshot.anchorOffset
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading])

  useEffect(() => {
    if (skipAutoScrollRef.current) {
      skipAutoScrollRef.current = false
      return
    }
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages, isProcessing, progressText])

  if (isLoading) {
    return (
      <div className={styles.thread}>
        <div className={styles.loading}>
          <Loader width={40} height={40} />
        </div>
      </div>
    )
  }

  return (
    <div ref={threadRef} className={styles.thread} onScroll={saveScrollSnapshot}>
      {isEmpty ? (
        <StarterPanel
          profiles={agentProfiles}
          agentProfile={agentProfile}
          disabled={isProcessing}
          onAgentProfileChange={onAgentProfileChange}
          onSelect={onSuggestionSelect}
        />
      ) : (
        <div ref={turnsRef} className={styles.turns}>
          {turns.map((turn, index) => {
            const isLastTurn = index === turns.length - 1

            if (turn.type === 'user') {
              const isLastUser = lastUser?.ulid === turn.message.ulid
              const canAct = !isProcessing && isLastUser && !isOptimisticMessage(turn.message)

              return (
                <MessageBubble
                  key={turn.message.ulid}
                  message={turn.message}
                  showEdit={canAct}
                  showRetry={canAct && !hasMessagesAfter(messages, turn.message.ulid)}
                  onEdit={() => onEditUserMessage?.(turn.message.ulid)}
                  onRetry={onRetryLastTurn}
                />
              )
            }

            return (
              <AssistantTurn
                key={`assistant-${index}-${turn.messages[0]?.ulid ?? index}`}
                messages={turn.messages}
                compactChart={compactChart}
                onChartExpand={onChartExpand}
                onRetry={!isProcessing && isLastTurn ? onRetryLastTurn : undefined}
              />
            )
          })}

          {/* Les messages arrivent un par un pendant le tour : la progression reste affichée jusqu'au done. */}
          {isProcessing && (
            <MessageBubble
              progressText={progressText ?? 'Analyse en cours…'}
              showStop
              onStop={onStop}
            />
          )}
        </div>
      )}

      {turnError && (
        <div className={styles.turnErrorRow}>
          <div className={styles.turnError}>{turnError}</div>
          {onRetryTurnError && (
            <RoundedButton
              label="Réessayer"
              theme="transparent-dark"
              size="nano"
              onClick={onRetryTurnError}
              testId="button-stats_assistant-retry_error"
            />
          )}
        </div>
      )}
      <div ref={bottomRef} />
    </div>
  )
}
